import type { UISchemaElement } from "@jsonforms/core";

interface SchemaWithProperties {
  properties?: Record<string, unknown>;
}

type LayoutElement = UISchemaElement & {
  elements?: UISchemaElement[];
  scope?: string;
};

const SCOPE_PREFIX = "#/properties/";
const NESTED = "/properties/";

/**
 * The data path a scope addresses, in the form JSON Forms uses for it.
 *
 * <p>Most scopes name one property, but a field inside an object names two —
 * `#/properties/quality_control/properties/qc_safety_date` is the path `quality_control.qc_safety_date`,
 * which is what JSON Forms passes as the control's `path` and therefore what every draft, decision
 * and finding here is keyed by. Reading only the first segment silently dropped those controls: the
 * projection could not find a property called `quality_control/properties/qc_safety_date`, so the
 * six quality-control fields of a product rendered nowhere at all.
 */
export function scopePath(scope: string | undefined): string | null {
  return scope?.startsWith(SCOPE_PREFIX)
    ? scope.slice(SCOPE_PREFIX.length).split(NESTED).join(".")
    : null;
}

/** Whether the schema declares the property this path addresses, however deep it is. */
function declares(schema: SchemaWithProperties, path: string): boolean {
  let current: SchemaWithProperties | undefined = schema;
  for (const segment of path.split(".")) {
    const properties = current?.properties;
    if (!properties || !Object.hasOwn(properties, segment)) return false;
    current = properties[segment] as SchemaWithProperties;
  }
  return true;
}

function projectElement(element: LayoutElement, schema: SchemaWithProperties,
                        kept: ReadonlySet<string>): UISchemaElement | null {
  if (element.type === "Control") {
    // A control the caller draws itself stays, whatever the schema says: a product's dosage
    // regimens are a collection rather than a value, so the scalar schema has no property for
    // them and dropping the control would drop the group the layout put them in.
    if (element.scope && kept.has(element.scope)) return element;
    const path = scopePath(element.scope);
    return path && declares(schema, path) ? element : null;
  }
  if (!element.elements) return element;
  const elements = element.elements
    .map((child: UISchemaElement) => projectElement(child as LayoutElement, schema, kept))
    .filter((child: UISchemaElement | null): child is UISchemaElement => child !== null);
  return elements.length ? { ...element, elements } : null;
}

/** Projects an authored layout onto the response-resolved scalar schema. */
export function projectScalarUiSchema(uiSchema: UISchemaElement, schema: SchemaWithProperties,
                                      keepScopes: readonly string[] = []): UISchemaElement {
  return projectElement(uiSchema as LayoutElement, schema, new Set(keepScopes))
    ?? { type: "VerticalLayout", elements: [] };
}
